import React from 'react';
import _ from 'underscore';

import { AccelerationTasks } from '~src/dataAcceleration/resources/AccelerationTasks';
import { NumberInputRange } from '~src/settings/general-settings/utils';

import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { disabledText, formError, narrowTextFieldStyle } from '../../../../styles/CommonStyles';
import { DataAccelerationSectionProps } from './DataAccelerationSection';

export const MAX_VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION = NumberInputRange.LONG.MAX;
export const VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD = 'settings.workbookAccelerationSettings.viewsLimitPerSiteForAcceleration';
export const VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD_ENABLED = 'settings.workbookAccelerationSettings.viewsLimitPerSiteForAccelerationEnabled';

export const VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_DEBOUNCE_MS = 1000;
export const UNINITIALIZED_NUM_ENABLED_SHEETS = -1;

export function DataAccelerationViewsLimitPerSiteField(props: DataAccelerationSectionProps) {
  const formik = useFormikContext<DataAccelerationSectionProps>();

  var minViewsLimitPerSiteForAcceleration = React.useRef(UNINITIALIZED_NUM_ENABLED_SHEETS);

  // Don't show the error right away until after the user finishes typing
  // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedFieldTouched = React.useCallback(_.debounce(() => {
    formik.setFieldTouched(VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD, true);
  }, VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_DEBOUNCE_MS), [formik.setFieldTouched]);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const viewsLimitPerSiteForAccelerationCount = (+e.target.value);
      const viewsLimitPerSiteForAccelerationCountError = viewsLimitPerSiteValidator(
        viewsLimitPerSiteForAccelerationCount, minViewsLimitPerSiteForAcceleration.current);
      const value = viewsLimitPerSiteForAccelerationCountError === null ? viewsLimitPerSiteForAccelerationCount : e.target.value;
      formik.setFieldValue(VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD, value);
      setDebouncedFieldTouched();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue, setDebouncedFieldTouched]
  );

  const disabled = !formik.values.settings.workbookAccelerationSettings?.viewsLimitPerSiteForAccelerationEnabled;
  const validate = React.useCallback((value: string) => disabled ? null :
    viewsLimitPerSiteValidator(+value, minViewsLimitPerSiteForAcceleration.current),
    [disabled, minViewsLimitPerSiteForAcceleration]);

  React.useEffect(
    () => {
      async function fetchNumEnabledSheets() {
        // Call to server for the current number of enabled sheets
        const response = await AccelerationTasks.findNumberOfEnabledSheets();
        if (response.errors === undefined) {
          minViewsLimitPerSiteForAcceleration.current = response.numOfEnabledSheets;
        }
      }

      if (minViewsLimitPerSiteForAcceleration.current === UNINITIALIZED_NUM_ENABLED_SHEETS) {
        fetchNumEnabledSheets();
      }
    },[minViewsLimitPerSiteForAcceleration]
  );

  // Explicitly set viewsLimitPerSiteForAcceleration to undefined when it has error and viewsLimitPerSiteForAcceleration change,
  // so that form validation can happen and form saving can be disabled properly.
  React.useEffect(
    () => {
      const viewsLimitPerSiteForAccelerationCountError = viewsLimitPerSiteValidator(
        formik.values.settings.workbookAccelerationSettings?.viewsLimitPerSiteForAcceleration, minViewsLimitPerSiteForAcceleration.current);
      if (viewsLimitPerSiteForAccelerationCountError) {
        formik.setFieldValue(VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD, undefined);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, formik.setFieldValue, minViewsLimitPerSiteForAcceleration]
  );

  const viewsLimitPerSiteForAccelerationCountError = viewsLimitPerSiteValidator(
    formik.values.settings.workbookAccelerationSettings?.viewsLimitPerSiteForAcceleration, minViewsLimitPerSiteForAcceleration.current);
  const viewsLimitPerSiteForAccelerationError = formik.errors?.settings?.workbookAccelerationSettings?.viewsLimitPerSiteForAcceleration;

  return <>
    <div>
      <FormikTextBox testId={'views-limit-per-site-for-acceleration-input'}
                     name={VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD}
                     label={null}
                     aria-label={Messages.limitNumberOfAcceleratedViews()}
                     validate={validate}
                     showValidationMessage={false}
                     disableOnSubmit={true}
                     className={narrowTextFieldStyle}
                     value={formik.values.settings.workbookAccelerationSettings?.viewsLimitPerSiteForAcceleration ?? ''}
                     disabled={disabled}
                     maxLength={9}
                     required={true}
                     onChange={handleChange}
      />
      &nbsp;
      <span className={disabled ? disabledText : undefined}></span>
    </div>
    {!disabled && formik.touched?.settings?.workbookAccelerationSettings?.viewsLimitPerSiteForAcceleration && viewsLimitPerSiteForAccelerationError &&
    <div data-tb-test-id={'views-limit-per-site-for-acceleration-error'} className={formError}>
      {viewsLimitPerSiteForAccelerationError}
    </div>}
  </>;
}

function isValidViewsLimit(viewsLimitPerSiteValue: number) {
  return !isNaN(viewsLimitPerSiteValue) && viewsLimitPerSiteValue >= 0 && isFinite(viewsLimitPerSiteValue);
}

/** Check if a given value is a valid input for views limit to be set */
export const viewsLimitPerSiteValidator = (viewsLimitPerSiteValue: number, viewsLimitPerSiteMinThreshold: number) => {
  if (!isValidViewsLimit(viewsLimitPerSiteValue)) {
    return Messages.viewsLimitPerSiteMustBeWholeNumber();
  }

  if (viewsLimitPerSiteValue > MAX_VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION) {
    return Messages.viewsLimitPerSiteMustBeLessThanMax();
  }

  if (viewsLimitPerSiteValue < viewsLimitPerSiteMinThreshold) {
    return Messages.viewsLimitPerSiteMustBeGreaterThanCurrentNumberOfEnabledSheets(
        {NUM_VIEWS: viewsLimitPerSiteMinThreshold.toString()});
  }
  return null;
};
